/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/modals/emailComposerModal.js
 * @function  openEmailComposer
 * @layer     ui-modal
 * @summary   Opens an email composer modal pre-filled with a rendered template.
 *
 * @description
 * • If `templateId` is provided, we call `TemplateService.render()` **once** with
 *   a context containing both `templateData` (step-specific) and `mergeVars`
 *   (Contact, Company, User, Date).
 * • That render returns the fully-personalised subject & HTML you want the
 *   seller to see.
 * • No merge-var substitution is attempted after this. Whatever lands in the
 *   `<textarea>` and `<input>` is exactly what is sent back on **Send**.
 *
 * ## Public API
 * ```ts
 * openEmailComposer({
 *   to:            string,                   // display-only
 *   subject?:      string,                   // fallback when no templateId
 *   templateHtml?: string,                   // fallback when no templateId
 *   templateId?:   string,                   // Firestore doc id
 *   templateData?: Record<string, unknown>,  // per-step vars
 *   mergeVars?:    Record<string, unknown>,  // Contact / Company / User / Date
 *   brainstormContext?: {
 *     contactReport?: string,
 *     companyReport?: string,
 *     mergeVars?: Record<string, unknown>,
 *     templateId?: string
 *   },                                        // Focus Mode coaching context
 *   stepTemplateManagement?: {
 *     taskId: string,
 *     playbookId?: string,
 *     stepName?: string,
 *     tenantId?: string,
 *     isTargetBacked?: boolean,
 *     onSaved?: Function
 *   }                                         // Focus Mode step template editor
 * }): Promise<{ subject:string, html:string } | null>
 * ```
 *
 * ## Dependencies
 * – `ModalService`    (di.modal)
 * – `TemplateService` (di.templateService)
 *
 * @author    TopSail AI
 * @version   1.5.0
 * @since     2025-05-11
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { TemplateUpdateFlow } from './templateUpdateFlow.js';
import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';
import {
  formatEmailAttachmentSize,
  normalizeEmailAttachments,
  validateEmailAttachmentSelection,
} from '../utils/emailAttachmentUtils.js';

const OUTLOOK_SAFE_FONTS = [
  {
    label: 'Aptos',
    value: 'aptos',
    stack: '"Aptos", "Calibri", "Segoe UI", sans-serif',
  },
  {
    label: 'Calibri',
    value: 'calibri',
    stack: '"Calibri", "Segoe UI", sans-serif',
  },
  {
    label: 'Arial',
    value: 'arial',
    stack: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  },
  {
    label: 'Times New Roman',
    value: 'times-new-roman',
    stack: '"Times New Roman", Times, serif',
  },
  {
    label: 'Verdana',
    value: 'verdana',
    stack: 'Verdana, Geneva, sans-serif',
  },
  {
    label: 'Tahoma',
    value: 'tahoma',
    stack: 'Tahoma, Geneva, sans-serif',
  },
  {
    label: 'Georgia',
    value: 'georgia',
    stack: 'Georgia, Times, "Times New Roman", serif',
  },
  {
    label: 'Trebuchet',
    value: 'trebuchet',
    stack: '"Trebuchet MS", Arial, sans-serif',
  },
];

const DEFAULT_FONT = 'trebuchet';
const FONT_STORAGE_KEY = 'topsail.emailComposer.font';
const FONT_VALUE_SET = new Set(OUTLOOK_SAFE_FONTS.map((font) => font.value));
const FONT_STACK_MAP = new Map(
  OUTLOOK_SAFE_FONTS.map(({ value, stack }) => [value, stack])
);
const DEFAULT_BRAINSTORM_EMPTY_TEXT = '';

const FONT_SIZE_OPTIONS = [
  { label: 'Default (16 px)', value: '' },
  { label: '12 px', value: '12px' },
  { label: '14 px', value: '14px' },
  { label: '16 px', value: '16px' },
  { label: '18 px', value: '18px' },
  { label: '20 px', value: '20px' },
  { label: '24 px', value: '24px' },
  { label: '28 px', value: '28px' },
  { label: '32 px', value: '32px' },
];

const DEFAULT_FONT_SIZE = '16px';
const DEFAULT_QUILL_LOAD_TIMEOUT_MS = 15_000;
const FONT_SIZE_VALUE_SET = new Set(
  FONT_SIZE_OPTIONS.filter(({ value }) => value).map(({ value }) => value)
);
const FONT_SIZE_LABEL_MAP = new Map(
  FONT_SIZE_OPTIONS.map(({ value, label }) => [value || '', label])
);
const SAFE_REWRITE_HTML_TAGS = new Set([
  'p',
  'br',
  'strong',
  'em',
  'u',
  'ol',
  'ul',
  'li',
  'a',
  'img',
]);
const STRIP_REWRITE_HTML_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'form',
  'input',
  'button',
]);
const REWRITE_TAG_ALIAS_MAP = new Map([
  ['b', 'strong'],
  ['i', 'em'],
]);
const SAFE_REWRITE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
const SAFE_REWRITE_IMAGE_PROTOCOLS = new Set(['http:', 'https:']);
const SAFE_REWRITE_DIMENSION_PATTERN = /^\d{1,4}$/;
const SAFE_REWRITE_FALLBACK_TEXT_RATIO = 0.5;

function loadStoredFont() {
  try {
    const raw = localStorage.getItem(FONT_STORAGE_KEY);
    if (FONT_VALUE_SET.has(raw)) return raw;
  } catch (err) {
    console.debug('[EmailComposer] font storage read failed', err);
  }
  return DEFAULT_FONT;
}

function persistSelectedFont(value) {
  try {
    localStorage.setItem(FONT_STORAGE_KEY, value);
  } catch (err) {
    console.debug('[EmailComposer] font storage write failed', err);
  }
}

function applyFontToEditor(quillInstance, fontValue) {
  if (!quillInstance) return;
  const root = quillInstance.root;
  OUTLOOK_SAFE_FONTS.forEach(({ value }) => {
    root.classList.toggle(`ql-font-${value}`, value === fontValue);
  });
  const stack =
    FONT_STACK_MAP.get(fontValue) || FONT_STACK_MAP.get(DEFAULT_FONT);
  if (stack) {
    root.style.fontFamily = stack;
  }
}

/**
 * Apply a consistent base font-size to the Quill editor root.
 * @param {import('quill') | null} quillInstance - Active Quill instance.
 * @param {string} fontSize - CSS font-size (px) or empty for default rendering.
 * @returns {void}
 */
function applyFontSizeToEditor(quillInstance, fontSize) {
  if (!quillInstance) return;
  const { root } = quillInstance;
  if (fontSize) {
    root.style.fontSize = fontSize;
    return;
  }
  root.style.removeProperty('font-size');
}

/**
 * Load the Quill editor bundle when it is not already present.
 *
 * A temporary `window.__tsLoadQuill` hook is supported for tests so the modal
 * can exercise the fallback editor path without reaching the CDN.
 *
 * @param {{ timeoutMs?: number }} [options] - Loader timeout configuration.
 * @returns {Promise<boolean>} True when Quill is available on `window`.
 */
async function ensureQuillLoaded(options = {}) {
  if (window.Quill) return true;

  const timeoutMs = Number(options.timeoutMs || DEFAULT_QUILL_LOAD_TIMEOUT_MS);
  const loadQuill =
    typeof window.__tsLoadQuill === 'function'
      ? window.__tsLoadQuill
      : async () => {
          await import(
            'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.min.js'
          );

          if (!document.getElementById('quill-css')) {
            const link = document.createElement('link');
            link.id = 'quill-css';
            link.rel = 'stylesheet';
            link.href =
              'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.snow.css';
            document.head.appendChild(link);
          }
        };

  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Timed out loading rich-text editor'));
    }, timeoutMs);

    Promise.resolve()
      .then(() => loadQuill())
      .then(() => {
        clearTimeout(timer);
        resolve();
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });

  return Boolean(window.Quill);
}

let emailComposerAssetsPromise = null;

/**
 * Preload rich email composer assets without opening the composer modal.
 *
 * @param {{timeoutMs?: number}} [options={}] Loader timeout configuration.
 * @returns {Promise<boolean>} True when the rich editor bundle is ready.
 */
export function preloadEmailComposerAssets(options = {}) {
  if (!emailComposerAssetsPromise) {
    emailComposerAssetsPromise = ensureQuillLoaded(options).catch((err) => {
      emailComposerAssetsPromise = null;
      throw err;
    });
  }
  return emailComposerAssetsPromise;
}

export function openEmailComposer({
  to,
  templateId = '',
  templateIds = [],
  templateData = {},
  mergeVars = {},
  templateHtml = '',
  subject = '',
  manual = false,
  goOpts = false,
  brainstormContext = null,
  stepTemplateManagement = null,
  allowAttachments = false,
  enableTooltips = typeof document !== 'undefined' &&
    document.body?.classList.contains('focusmode') &&
    document.body?.getAttribute('data-focus-tooltips') !== 'false',
}) {
  /* ── DEBUG helper ────────────────────────────────────────── */
  const dbg = (...args) => console.debug('[EmailComposer]', ...args); // always on for now

  /* These two vars will always contain what the seller sees AND what is sent */
  let viewSubject = subject;
  let viewHtml = templateHtml;
  let viewFontFamily = loadStoredFont();
  let rawSubject = subject;
  let rawHtml = templateHtml;
  let rawFontFamily = viewFontFamily;
  let selectedFont = loadStoredFont();
  let templateEditMode = false;
  let updateEnabled = true;
  let templateAttachments = [];
  let adHocAttachments = [];
  let renderedTemplateSnapshot = {
    subject: '',
    bodyText: '',
    html: '',
  };

  let allTemplates = normalizeEmailComposerTemplateIds(templateIds);
  if (templateId && !allTemplates.includes(templateId)) {
    allTemplates.unshift(templateId);
  }
  let currentTpl = templateId || allTemplates[0] || '';
  const canUpdateTemplate = Boolean(currentTpl);
  const canManageStepTemplates = Boolean(
    stepTemplateManagement?.taskId && allTemplates.length
  );

  return new Promise(async (resolve) => {
    /* DI singletons --------------------------------------------------------- */
    const di = (await import('../bootstrap/app.js')).default;
    const modalService = di.modalService || di.modal;
    const brainstormCallable = di.callables?.generateEmailBrainstormIdeas;
    const rewriteCallable = di.callables?.rewriteEmailDraft;
    const canBrainstorm = Boolean(
      brainstormContext &&
        typeof brainstormContext === 'object' &&
        brainstormContext?.brainstormEnabled !== false &&
        brainstormCallable
    );
    const canRewrite = Boolean(
      brainstormContext &&
        typeof brainstormContext === 'object' &&
        brainstormContext?.rewriteEnabled !== false &&
        rewriteCallable
    );
    const canAiAssist = canBrainstorm || canRewrite;

    const templateUpdateFlow = new TemplateUpdateFlow({
      di,
      modalService,
      templateService: di.templateService,
      templateData,
      mergeVars,
      initialSubject: viewSubject,
      initialHtml: viewHtml,
      initialFontFamily: viewFontFamily,
      defaultFontFamily: DEFAULT_FONT,
      includeSubject: true,
      hostModalId: 'emailComposerModal',
      confirmModalId: 'emailTemplateUpdateConfirmModal',
      alertModalId: 'emailComposerAlertModal',
      normalizeRendered: fixUtf8,
    });

    async function loadTemplate(id) {
      if (!id) {
        templateAttachments = [];
        return;
      }
      try {
        dbg('templateId      =', id);
        dbg('mergeVars       =', mergeVars);
        dbg('templateData    =', templateData);

        const loadResult = await templateUpdateFlow.loadTemplate(id);
        templateAttachments = normalizeEmailAttachments(
          loadResult?.attachments
        );
        updateEnabled = templateUpdateFlow.updateEnabled;
        const rendered = templateUpdateFlow.getRenderedView();
        const raw = templateUpdateFlow.getRawSource();
        viewSubject = rendered.subject ?? viewSubject;
        viewHtml = rendered.html ?? viewHtml;
        if (rendered.fontFamily && FONT_VALUE_SET.has(rendered.fontFamily)) {
          viewFontFamily = rendered.fontFamily;
          selectedFont = rendered.fontFamily;
        }
        rawSubject = raw.subject ?? rawSubject;
        rawHtml = raw.html ?? rawHtml;
        rawFontFamily = raw.fontFamily ?? rawFontFamily;

        dbg('rendered.subject =', viewSubject);
        dbg(
          'rendered.html    =',
          (viewHtml || '').slice(0, 160),
          viewHtml && viewHtml.length > 160 ? '…' : ''
        );
        renderedTemplateSnapshot = {
          subject: viewSubject,
          bodyText: htmlToPlainText(viewHtml),
          html: viewHtml,
        };
        if (loadResult?.renderError) {
          console.warn(
            '[EmailComposer] template preview render failed:',
            loadResult.renderError
          );
          di.errorUI?.global?.({
            text: 'Template preview could not render. Showing raw template syntax.',
            type: 'warning',
            duration: 4000,
          });
        }
      } catch (err) {
        templateAttachments = [];
        console.error('[EmailComposer] template render failed:', err);
      }
    }

    await loadTemplate(currentTpl);
    renderedTemplateSnapshot = {
      subject: viewSubject,
      bodyText: htmlToPlainText(viewHtml),
      html: viewHtml,
    };

    /* 2️⃣  Ensure modal host exists ---------------------------------------- */
    let host = document.getElementById('emailComposerModal');
    if (!host) {
      host = document.createElement('div');
      host.id = 'emailComposerModal';
      host.className = 'hidden modal-overlay'; // ← add this part
      document.body.appendChild(host);
      modalService.register('emailComposerModal', host);
    }

    /* 3️⃣  Inject markup ---------------------------------------------------- */
    host.innerHTML = /* html */ `
      <div class="modal-container modal-teal">
        <div class="ec-header focus-modal__header">
          <div class="focus-modal__title-group">
            <h3 class="ec-header__title">Email → ${to || 'recipient'}</h3>
          </div>
          <div class="ec-header__actions focus-modal__header-actions">
            ${
              canUpdateTemplate
                ? `<button id="ec-edit-template" class="btn-pill btn--grey btn--info-sm" data-focus-tooltip="Edit the selected email template.">
                            Edit Template
                          </button>
                          <button id="ec-update-template" class="btn-pill btn--grey btn--info-sm hidden" data-focus-tooltip="Save changes to the selected email template.">
                            Update Template
                          </button>`
                : ''
            }
            ${
              canAiAssist
                ? `<div class="ec-ai-actions">
                            ${
                              canBrainstorm
                                ? `<button
                                     id="ec-brainstorm"
                                     class="btn-pill btn--grey btn--info-sm btn--secondary-action ec-brainstorm-trigger"
                                     aria-describedby="ec-brainstorm-spinner"
                                     data-focus-tooltip="Generate coaching ideas from the current Focus Mode context."
                                   >
                                     <span
                                       id="ec-brainstorm-spinner"
                                       class="spinner-inline hidden"
                                       aria-hidden="true"
                                     ></span>
                                     <span id="ec-brainstorm-text">Brainstorm Ideas</span>
                                   </button>`
                                : ''
                            }
                            ${
                              canRewrite
                                ? `<button
                                     id="ec-rewrite"
                                     class="btn-pill btn--grey btn--info-sm btn--secondary-action ec-rewrite-trigger"
                                     aria-describedby="ec-rewrite-spinner"
                                     data-focus-tooltip="Rewrite the draft using AI."
                                   >
                                     <span
                                       id="ec-rewrite-spinner"
                                       class="spinner-inline hidden"
                                       aria-hidden="true"
                                     ></span>
                                     <span id="ec-rewrite-text">Rewrite</span>
                                   </button>
                                   <button
                                     id="ec-undo-rewrite"
                                     class="btn-pill btn--grey btn--info-sm btn--secondary-action ec-undo-rewrite hidden"
                                     type="button"
                                     data-focus-tooltip="Restore the draft before the last rewrite."
                                   >
                                     Undo Rewrite
                                   </button>`
                                : ''
                            }
                          </div>`
                : ''
            }
          </div>
        </div>
        <p
          id="ec-template-mode"
          class="focus-modal__mode-banner text-xs text-gray-200 mb-3 hidden"
        >
          Template edit mode: sending is disabled.
        </p>
        <p
          id="ec-template-disabled"
          class="focus-modal__mode-banner text-xs text-gray-200 mb-3 hidden"
        >
          Template update unavailable. Check tenant access or template permissions.
        </p>
        <div class="modal-body">
        <div class="ec-compose-layout${
          canAiAssist
            ? ' ec-compose-layout--rail-collapsed'
            : ' ec-compose-layout--single'
        }">
          ${
            canAiAssist
              ? `<aside id="ec-brainstorm-panel" class="ec-brainstorm-shell ec-brainstorm-shell--collapsed" aria-live="polite">
                   <section class="ec-brainstorm hidden" aria-label="AI Brainstorm Ideas" aria-hidden="true">
                     <p id="ec-brainstorm-empty" class="ec-brainstorm__empty${
                       DEFAULT_BRAINSTORM_EMPTY_TEXT ? '' : ' hidden'
                     }">
                       ${escapeAttr(DEFAULT_BRAINSTORM_EMPTY_TEXT)}
                     </p>
                     <p
                       id="ec-brainstorm-status"
                       class="ec-brainstorm__status hidden"
                       role="status"
                       aria-live="polite"
                     ></p>
                     <div id="ec-brainstorm-ideas" class="ec-brainstorm__ideas"></div>
                   </section>
                 </aside>`
              : ''
          }

          <div class="ec-email-column">
            <div class="ec-fields">
              <div
                id="ec-template-row"
                class="flex items-center gap-2 mb-4${
                  allTemplates.length > 1 || canManageStepTemplates
                    ? ''
                    : ' hidden'
                }"
              >
                <label for="ec-template" class="text-sm font-medium shrink-0">
                  Template
                </label>
                <select id="ec-template" class="input flex-1 h-8 px-2 text-sm">
                  ${renderTemplateOptions(allTemplates, currentTpl)}
                </select>
                ${
                  canManageStepTemplates
                    ? `<button
                         id="ec-manage-step-templates"
                         type="button"
                         class="btn-pill btn--grey btn--info-sm"
                         data-focus-tooltip="Add, remove, or reorder templates for this step."
                       >Add template</button>`
                    : ''
                }
              </div>

              <div class="flex items-center gap-2 mb-4">
                <label for="ec-subject" class="text-sm font-medium shrink-0">
                  Subject
                </label>
                <input id="ec-subject"
                       class="input flex-1 h-12 px-3 text-base"
                       style="min-width:18rem"
                       value="${escapeAttr(viewSubject)}" />
              </div>

              <div id="ec-font-family-control" class="ec-font-family-control flex items-center gap-2 mb-2">
                <label for="ec-font-family" class="text-sm font-medium shrink-0">Font</label>
                <select id="ec-font-family" class="input flex-1 h-8 px-2 text-sm">
                  ${OUTLOOK_SAFE_FONTS.map(
                    ({ label, value }) =>
                      `<option value="${value}" ${
                        value === selectedFont ? 'selected' : ''
                      }>${label}</option>`
                  ).join('')}
                </select>
              </div>
            </div>

            <section class="ec-compose-main">
              <label class="block text-sm font-medium mb-1">Body</label>
              <div class="ec-editor-shell">
                <div id="ec-body" class="quill-editor bg-white"></div>
                <textarea
                  id="ec-body-raw"
                  class="input w-full min-h-[160px] hidden"
                  aria-label="Template body source"
                ></textarea>
              </div>

              <p class="mt-2 text-xs text-gray-500">
                Your saved signature will be appended on send.
              </p>

              ${
                allowAttachments
                  ? `
              <section class="ec-attachments" id="ec-attachments">
                <div class="ec-attachments__header">
                  <button
                    id="ec-attach"
                    type="button"
                    class="btn btn--secondary btn--info-sm"
                    data-focus-tooltip="Attach files to this email only."
                  >Attach Files</button>
                  <span id="ec-attachment-summary" class="ec-attachments__summary">No attachments</span>
                  <input
                    id="ec-attachment-input"
                    class="hidden"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,image/png,image/jpeg,image/webp"
                  />
                </div>
                <ul id="ec-attachment-list" class="ec-attachments__list"></ul>
              </section>`
                  : ''
              }
            </section>

            <footer class="ec-footer focus-modal__footer">
              <div class="focus-modal__footer-actions" data-actions="2">
                <button id="ec-cancel" class="btn-pill btn--grey btn--info-sm btn--secondary-action" data-focus-tooltip="Close without sending this email.">Cancel</button>
                <button id="ec-send" class="btn-pill btn--orange btn--info-sm btn--primary-action" data-focus-tooltip="${manual ? 'Record this email as manually sent.' : 'Send this email and continue.'}">
                  ${manual ? 'Manually Sent' : 'Send'}
                </button>
              </div>
            </footer>

            ${
              goOpts
                ? `
            <section id="ec-go-opts"
                     class="go-opts-card mt-6 space-y-3">
              <label class="block">
                <input id="ec-meeting" type="checkbox" class="mr-2">
                Meeting&nbsp;Invite&nbsp;Sent
              </label>
              <label class="block">
                <input id="ec-event" type="checkbox" class="mr-2">
                Event&nbsp;Invite&nbsp;Sent
              </label>
              <label class="block">
                <input id="ec-other-check" type="checkbox" class="mr-2">
                Other
              </label>

              <input id="ec-other-text"
                     class="input hidden w-full"
                     placeholder="Specify…" />

              <button id="ec-save"
                      class="btn-pill btn--teal btn--info-sm btn--primary-action w-full hidden mt-3"
                      data-focus-tooltip="Save selected GO follow-up details.">
                Save
              </button>
            </section>`
                : ''
            }
          </div>
          <aside
            id="ec-template-guide"
            class="ec-template-guide hidden"
            aria-label="Template merge variables"
            aria-hidden="true"
          >
            <h4>Personalization guide</h4>
            <dl>
              <div><dt><code>{{Contact.firstName}}</code></dt><dd>Contact first name</dd></div>
              <div><dt><code>{{Contact.lastName}}</code></dt><dd>Contact last name</dd></div>
              <div><dt><code>{{Contact.fullName}}</code></dt><dd>Contact full name</dd></div>
              <div><dt><code>{{Contact.title}}</code></dt><dd>Job title</dd></div>
              <div><dt><code>{{Contact.companyName}}</code></dt><dd>Company name</dd></div>
              <div><dt><code>{{Company.website}}</code></dt><dd>Company website</dd></div>
              <div><dt><code>{{User.firstName}}</code></dt><dd>Your first name</dd></div>
              <div><dt><code>{{User.email}}</code></dt><dd>Your email</dd></div>
              <div><dt><code>{{Date.today}}</code></dt><dd>Today's date</dd></div>
            </dl>
          </aside>
        </div>
        </div>
      </div>`;

    applyFocusModeTooltips(host, { enableTooltips });

    /* 4️⃣  Wire up buttons -------------------------------------------------- */
    const $send = host.querySelector('#ec-send');
    const $cancel = host.querySelector('#ec-cancel');
    const $save = host.querySelector('#ec-save');
    const $meeting = host.querySelector('#ec-meeting');
    const $event = host.querySelector('#ec-event');
    const $tplSel = host.querySelector('#ec-template');
    const $tplRow = host.querySelector('#ec-template-row');
    const $manageStepTemplates = host.querySelector(
      '#ec-manage-step-templates'
    );
    const $otherChk = host.querySelector('#ec-other-check');
    const $otherTxt = host.querySelector('#ec-other-text');
    const $fontControl = host.querySelector('#ec-font-family-control');
    const $fontSel = host.querySelector('#ec-font-family');
    const $updateTemplate = host.querySelector('#ec-update-template');
    const $editTemplate = host.querySelector('#ec-edit-template');
    const $templateMode = host.querySelector('#ec-template-mode');
    const $templateDisabled = host.querySelector('#ec-template-disabled');
    const $templateGuide = host.querySelector('#ec-template-guide');
    const $subjectInput = host.querySelector('#ec-subject');
    const $composeLayout = host.querySelector('.ec-compose-layout');
    const $editorShell = host.querySelector('.ec-editor-shell');
    const $bodyContainer = host.querySelector('#ec-body');
    const $rawBody = host.querySelector('#ec-body-raw');
    const $brainstormPanel = host.querySelector('#ec-brainstorm-panel');
    const $brainstormSection = host.querySelector('.ec-brainstorm');
    const $brainstormBtn = host.querySelector('#ec-brainstorm');
    const $brainstormText = host.querySelector('#ec-brainstorm-text');
    const $brainstormSpinner = host.querySelector('#ec-brainstorm-spinner');
    const $rewriteBtn = host.querySelector('#ec-rewrite');
    const $rewriteText = host.querySelector('#ec-rewrite-text');
    const $rewriteSpinner = host.querySelector('#ec-rewrite-spinner');
    const $undoRewriteBtn = host.querySelector('#ec-undo-rewrite');
    const $brainstormEmpty = host.querySelector('#ec-brainstorm-empty');
    const $brainstormStatus = host.querySelector('#ec-brainstorm-status');
    const $brainstormIdeas = host.querySelector('#ec-brainstorm-ideas');
    const $attachmentButton = host.querySelector('#ec-attach');
    const $attachmentInput = host.querySelector('#ec-attachment-input');
    const $attachmentList = host.querySelector('#ec-attachment-list');
    const $attachmentSummary = host.querySelector('#ec-attachment-summary');

    const setBrainstormEmpty = (text = DEFAULT_BRAINSTORM_EMPTY_TEXT) => {
      if (!$brainstormEmpty) return;
      $brainstormEmpty.textContent = text;
      $brainstormEmpty.classList.toggle('hidden', !String(text || '').trim());
    };

    const hideBrainstormEmpty = () => {
      $brainstormEmpty?.classList.add('hidden');
    };

    const setTemplateControlsEnabled = (enabled) => {
      if ($updateTemplate) $updateTemplate.disabled = !enabled;
      if ($editTemplate) $editTemplate.disabled = !enabled;
      if ($templateDisabled) {
        $templateDisabled.classList.toggle('hidden', enabled);
      }
    };

    setTemplateControlsEnabled(updateEnabled);

    const close = (val = null) => {
      modalService.close('emailComposerModal');
      $send.removeEventListener('click', onSend);
      $cancel.removeEventListener('click', onCancel);
      $save?.removeEventListener('click', onSave);
      $meeting?.removeEventListener('change', updateSave);
      $event?.removeEventListener('change', updateSave);
      $otherChk?.removeEventListener('change', updateSave);
      $otherTxt?.removeEventListener('input', updateSave);
      $tplSel?.removeEventListener('change', tplChange);
      $manageStepTemplates?.removeEventListener('click', onManageStepTemplates);
      $fontSel?.removeEventListener('change', handleFontChange);
      $brainstormBtn?.removeEventListener('click', onBrainstorm);
      $rewriteBtn?.removeEventListener('click', onRewrite);
      $undoRewriteBtn?.removeEventListener('click', onUndoRewrite);
      $attachmentButton?.removeEventListener('click', onAttachClick);
      $attachmentInput?.removeEventListener('change', onAttachmentInputChange);
      $updateTemplate?.removeEventListener('click', onUpdateTemplate);
      $editTemplate?.removeEventListener('click', onEditTemplate);
      $templateGuide?.removeEventListener('copy', onTemplateGuideCopy);
      resolve(val);
    };

    let quill;
    let sizePickerLabel = null;
    let aiBusyAction = '';
    let lastRewriteSnapshot = null;
    const readCurrentHtml = () => {
      if (quill?.root) {
        return (quill.root.innerHTML || '').trim();
      }
      if ($rawBody && !$rawBody.classList.contains('hidden')) {
        return ($rawBody.value || '').trim();
      }
      return String(viewHtml || '').trim();
    };

    const showFallbackEditor = (initialHtml = viewHtml) => {
      if ($bodyContainer) {
        $bodyContainer.classList.add('hidden');
      }
      if ($rawBody) {
        $rawBody.value = initialHtml || '';
        $rawBody.classList.remove('hidden');
      }
    };

    /**
     * Apply a subject/body snapshot back into the composer UI.
     *
     * @param {{ subject?: string, html?: string }} [draft={}] - Draft snapshot to display.
     * @returns {void}
     */
    const applyComposerDraft = (draft = {}) => {
      if ($subjectInput && typeof draft.subject === 'string') {
        viewSubject = draft.subject;
        $subjectInput.value = draft.subject;
      }

      const nextHtml = typeof draft.html === 'string' ? draft.html : '';
      viewHtml = nextHtml;
      if ($rawBody) {
        $rawBody.value = nextHtml;
      }
      if (quill?.root) {
        quill.root.innerHTML = nextHtml;
      }
    };

    /**
     * Refresh the in-composer template selector after step-template saves.
     *
     * @returns {void}
     */
    const syncTemplateSelector = () => {
      if ($tplRow) {
        $tplRow.classList.toggle(
          'hidden',
          !(allTemplates.length > 1 || canManageStepTemplates)
        );
      }
      if (!$tplSel) return;
      $tplSel.innerHTML = renderTemplateOptions(allTemplates, currentTpl);
      $tplSel.value = currentTpl || allTemplates[0] || '';
      $tplSel.disabled = !allTemplates.length;
    };

    /**
     * Snapshot the current draft so rewrite actions can be undone.
     *
     * @returns {{ subject: string, html: string }} Current draft state.
     */
    const snapshotCurrentDraft = () => ({
      subject: $subjectInput?.value || '',
      html: readCurrentHtml(),
    });

    /**
     * Present an attachment validation error in the global toast surface.
     *
     * @param {string} text User-visible error text.
     * @returns {void}
     */
    const showAttachmentError = (text) => {
      di.errorUI?.global?.({
        text,
        type: 'error',
        duration: 4000,
      });
    };

    /**
     * Render saved template attachments and one-off email attachments.
     *
     * @returns {void}
     */
    const renderAttachments = () => {
      const attachments = [...templateAttachments, ...adHocAttachments];
      if ($attachmentSummary) {
        const total = attachments.reduce(
          (sum, file) => sum + Number(file.size || 0),
          0
        );
        $attachmentSummary.textContent = attachments.length
          ? `${attachments.length} attached (${formatEmailAttachmentSize(total)})`
          : 'No attachments';
      }
      if (!$attachmentList) return;
      $attachmentList.innerHTML = '';
      templateAttachments.forEach((attachment) => {
        const item = document.createElement('li');
        item.className = 'ec-attachment-item';

        const label = document.createElement('span');
        label.className = 'ec-attachment-item__label';
        label.textContent = `${attachment.name || 'Attachment'} (${formatEmailAttachmentSize(attachment.size)})`;

        const badge = document.createElement('span');
        badge.className = 'ec-attachment-item__badge';
        badge.textContent = 'Template';
        badge.title = 'Saved with this template and included automatically';

        item.append(label, badge);
        $attachmentList.append(item);
      });
      adHocAttachments.forEach((file, index) => {
        const item = document.createElement('li');
        item.className = 'ec-attachment-item';

        const label = document.createElement('span');
        label.className = 'ec-attachment-item__label';
        label.textContent = `${file.name || 'Attachment'} (${formatEmailAttachmentSize(file.size)})`;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className =
          'btn btn--secondary btn--info-sm ec-attachment-item__remove';
        remove.textContent = 'Remove';
        remove.addEventListener('click', () => {
          adHocAttachments.splice(index, 1);
          renderAttachments();
        });

        item.append(label, remove);
        $attachmentList.append(item);
      });
    };

    /**
     * Open the hidden file picker for ad hoc attachments.
     *
     * @returns {void}
     */
    const onAttachClick = () => {
      $attachmentInput?.click();
    };

    /**
     * Add selected files to the in-memory ad hoc attachment list.
     *
     * @returns {void}
     */
    const onAttachmentInputChange = () => {
      const selected = Array.from($attachmentInput?.files || []);
      if (!selected.length) return;
      const validation = validateEmailAttachmentSelection([
        ...templateAttachments,
        ...adHocAttachments,
        ...selected,
      ]);
      if (!validation.ok) {
        showAttachmentError(
          validation.reason || 'Attachment is not supported.'
        );
        if ($attachmentInput) $attachmentInput.value = '';
        return;
      }
      adHocAttachments = [...adHocAttachments, ...selected];
      if ($attachmentInput) $attachmentInput.value = '';
      renderAttachments();
    };

    /**
     * Hide the inline AI-assist status message without touching idea cards.
     *
     * @returns {void}
     */
    const hideAiAssistStatus = () => {
      if ($brainstormStatus) {
        $brainstormStatus.textContent = '';
        $brainstormStatus.dataset.tone = 'info';
        $brainstormStatus.classList.add('hidden');
      }
      syncBrainstormRail();
    };

    /**
     * Collapse or expand the brainstorm rail based on visible content.
     *
     * @returns {void}
     */
    const syncBrainstormRail = () => {
      const hasIdeas = Boolean($brainstormIdeas?.children.length);
      const hasStatus = Boolean(
        $brainstormStatus &&
          !$brainstormStatus.classList.contains('hidden') &&
          String($brainstormStatus.textContent || '').trim()
      );
      const shouldExpand = hasIdeas || hasStatus;

      if ($brainstormSection) {
        $brainstormSection.classList.toggle('hidden', !shouldExpand);
        $brainstormSection.setAttribute(
          'aria-hidden',
          shouldExpand ? 'false' : 'true'
        );
      }
      if ($brainstormPanel) {
        $brainstormPanel.classList.toggle(
          'ec-brainstorm-shell--collapsed',
          !shouldExpand
        );
      }
      if ($composeLayout && canAiAssist) {
        $composeLayout.classList.toggle(
          'ec-compose-layout--rail-collapsed',
          !shouldExpand
        );
      }
    };

    /**
     * Refresh brainstorm/rewrite controls for busy and undo states.
     *
     * @returns {void}
     */
    const syncAiAssistControls = () => {
      const isBusy = Boolean(aiBusyAction);
      const isBrainstorming = aiBusyAction === 'brainstorm';
      const isRewriting = aiBusyAction === 'rewrite';

      if ($brainstormBtn) {
        $brainstormBtn.disabled = templateEditMode || isBusy;
        if (isBrainstorming) $brainstormBtn.setAttribute('aria-busy', 'true');
        else $brainstormBtn.removeAttribute('aria-busy');
      }
      $brainstormSpinner?.classList.toggle('hidden', !isBrainstorming);
      if ($brainstormText) {
        $brainstormText.textContent = isBrainstorming
          ? 'Generating Ideas…'
          : 'Brainstorm Ideas';
      }

      if ($rewriteBtn) {
        $rewriteBtn.disabled = templateEditMode || isBusy;
        if (isRewriting) $rewriteBtn.setAttribute('aria-busy', 'true');
        else $rewriteBtn.removeAttribute('aria-busy');
      }
      $rewriteSpinner?.classList.toggle('hidden', !isRewriting);
      if ($rewriteText) {
        $rewriteText.textContent = isRewriting ? 'Rewriting…' : 'Rewrite';
      }

      if ($undoRewriteBtn) {
        const hasUndo = Boolean(lastRewriteSnapshot);
        $undoRewriteBtn.classList.toggle('hidden', !hasUndo);
        $undoRewriteBtn.disabled = templateEditMode || isBusy || !hasUndo;
      }
    };

    /**
     * Update the active AI-assist action state.
     *
     * @param {'' | 'brainstorm' | 'rewrite'} action - Active AI action name.
     * @returns {void}
     */
    const setAiAssistBusy = (action = '') => {
      aiBusyAction = action;
      syncAiAssistControls();
    };

    /**
     * Clear the one-level rewrite undo snapshot.
     *
     * @returns {void}
     */
    const clearRewriteUndo = () => {
      lastRewriteSnapshot = null;
      syncAiAssistControls();
    };

    const clearBrainstormResults = (
      emptyMessage = DEFAULT_BRAINSTORM_EMPTY_TEXT
    ) => {
      hideAiAssistStatus();
      if ($brainstormIdeas) {
        $brainstormIdeas.innerHTML = '';
      }
      setBrainstormEmpty(emptyMessage);
      syncBrainstormRail();
    };

    const showBrainstormStatus = (text, tone = 'info') => {
      hideBrainstormEmpty();
      if ($brainstormStatus) {
        $brainstormStatus.textContent = text;
        $brainstormStatus.dataset.tone = tone;
        $brainstormStatus.classList.remove('hidden');
      }
      syncBrainstormRail();
    };

    const currentDraftBodyText = () => {
      if ($rawBody && !$rawBody.classList.contains('hidden')) {
        return normalizeEditorText(htmlToPlainText($rawBody.value || ''));
      }
      if (!quill?.root) {
        return normalizeEditorText(viewHtml || '');
      }
      const editorText =
        typeof quill?.getText === 'function' ? quill.getText() : '';
      return normalizeEditorText(editorText || quill?.root?.innerHTML || '');
    };

    const currentDraftSignature = () =>
      JSON.stringify({
        templateId: currentTpl,
        subject: $subjectInput?.value || '',
        bodyText: currentDraftBodyText(),
      });

    const renderBrainstormIdeas = (ideas = []) => {
      hideBrainstormEmpty();
      hideAiAssistStatus();
      if ($brainstormIdeas) {
        $brainstormIdeas.innerHTML = ideas
          .map(
            (idea) => `
              <article class="ec-brainstorm__idea">
                <header class="ec-brainstorm__idea-header">
                  <h4>${escapeAttr(idea.title || 'Idea')}</h4>
                  <span class="ec-brainstorm__idea-category">${escapeAttr(
                    idea.category || 'Improvement'
                  )}</span>
                </header>
                <p><strong>Why:</strong> ${escapeAttr(idea.rationale || '')}</p>
                <p><strong>Try:</strong> ${escapeAttr(
                  idea.suggestedChange || ''
                )}</p>
                <blockquote>${escapeAttr(idea.exampleSnippet || '')}</blockquote>
              </article>
            `
          )
          .join('');
      }
      syncBrainstormRail();
    };

    const setSelectedFont = (
      value,
      { applyFormat = false, persist = false, force = false } = {}
    ) => {
      const next = FONT_VALUE_SET.has(value) ? value : DEFAULT_FONT;
      if (!force && next === selectedFont) {
        applyFontToEditor(quill, selectedFont);
        if ($fontSel && $fontSel.value !== selectedFont) {
          $fontSel.value = selectedFont;
        }
        return;
      }
      selectedFont = next;
      if (persist) {
        persistSelectedFont(selectedFont);
      }
      if ($fontSel && $fontSel.value !== selectedFont) {
        $fontSel.value = selectedFont;
      }
      applyFontToEditor(quill, selectedFont);
      if (applyFormat && quill) {
        quill.format('font', selectedFont);
      }
    };

    const handleFontChange = (event) => {
      setSelectedFont(event.target.value, {
        applyFormat: true,
        persist: true,
        force: true,
      });
    };

    const setFontSelection = (value) => {
      setSelectedFont(value, {
        applyFormat: true,
        persist: false,
        force: true,
      });
    };

    $fontSel?.addEventListener('change', handleFontChange);
    if ($fontSel && !FONT_VALUE_SET.has($fontSel.value)) {
      $fontSel.value = selectedFont;
    }

    /**
     * Build the shared AI-assist request payload from the current composer state.
     *
     * @returns {{
     *   tenantId: string,
     *   subject: string,
     *   bodyText: string,
     *   bodyHtml: string,
     *   templateSnapshot: { subject: string, bodyText: string, html: string },
     *   contactReport: string,
     *   companyReport: string,
     *   mergeVars: Record<string, unknown>,
     *   templateId: string,
     * }} Callable-ready request payload.
     */
    const buildAiAssistRequest = () => ({
      tenantId:
        di.tenant?.getTenantId?.() || di.auth?.currentUser?.tenantId || '',
      subject: $subjectInput?.value || '',
      bodyText: currentDraftBodyText(),
      bodyHtml: readCurrentHtml(),
      templateSnapshot: {
        subject: renderedTemplateSnapshot.subject,
        bodyText: renderedTemplateSnapshot.bodyText,
        html: renderedTemplateSnapshot.html || '',
      },
      contactReport: brainstormContext?.contactReport || '',
      companyReport: brainstormContext?.companyReport || '',
      mergeVars: brainstormContext?.mergeVars || mergeVars,
      templateId: currentTpl || brainstormContext?.templateId || '',
    });

    /**
     * Normalize a rewrite callable response into composer-ready content.
     *
     * @param {{ subject?: string, bodyHtml?: string, bodyText?: string }} [data={}] - Raw callable response payload.
     * @param {{ subject: string, html: string }} fallbackDraft - Current draft fallback.
     * @returns {{ subject: string, html: string }} Composer-ready rewrite payload.
     */
    const normalizeRewriteDraft = (data = {}, fallbackDraft) => {
      const nextBodyText =
        typeof data.bodyText === 'string' ? data.bodyText.trim() : '';
      const nextBodyHtml =
        typeof data.bodyHtml === 'string' ? data.bodyHtml.trim() : '';
      const sanitizedBodyHtml = sanitizeAiRewriteHtml(nextBodyHtml);
      const safeBodyHtml = shouldFallbackToPlainTextRewrite(
        sanitizedBodyHtml,
        nextBodyText
      )
        ? plainTextToHtml(nextBodyText)
        : sanitizedBodyHtml;

      return {
        subject:
          (typeof data.subject === 'string' && data.subject.trim()) ||
          fallbackDraft.subject,
        html:
          safeBodyHtml || plainTextToHtml(nextBodyText) || fallbackDraft.html,
      };
    };

    syncAiAssistControls();
    syncBrainstormRail();

    const updateSave = () => {
      if (!$save) return;
      const any =
        $meeting?.checked ||
        $event?.checked ||
        ($otherChk?.checked && $otherTxt?.value.trim());
      if ($otherChk) {
        $otherTxt.classList.toggle('hidden', !$otherChk.checked);
      }
      $save.classList.toggle('hidden', !any);
    };

    const onSave = () => {
      const alt = {
        meetingInvite: !!$meeting?.checked,
        eventInvite: !!$event?.checked,
      };
      if ($otherChk?.checked) {
        alt.other = ($otherTxt?.value || '').trim();
      }
      close({
        alt,
        altOnly: true,
        templateId: currentTpl,
        fontFamily: selectedFont,
      });
    };

    const onSend = () => {
      const alt = goOpts
        ? {
            meetingInvite: !!$meeting?.checked,
            eventInvite: !!$event?.checked,
            ...($otherChk?.checked
              ? { other: ($otherTxt?.value || '').trim() }
              : {}),
          }
        : undefined;
      const payload = {
        subject: host.querySelector('#ec-subject').value,
        html: readCurrentHtml(),
        templateId: currentTpl,
        fontFamily: selectedFont,
        ...(adHocAttachments.length
          ? { adHocAttachments: [...adHocAttachments] }
          : {}),
        ...(alt ? { alt } : {}),
      };
      dbg('SEND payload =', payload);
      close(payload);
    };

    const onCancel = () => {
      dbg('Modal cancelled by user');
      close(null);
    };

    const setTemplateEditMode = async (nextMode) => {
      templateEditMode = nextMode;
      templateUpdateFlow.isEditing = templateEditMode;
      $composeLayout?.classList.toggle(
        'ec-compose-layout--template-edit',
        templateEditMode
      );
      $brainstormPanel?.classList.toggle(
        'ec-brainstorm-shell--template-edit-hidden',
        templateEditMode
      );
      if ($templateGuide) {
        $templateGuide.classList.toggle('hidden', !templateEditMode);
        $templateGuide.setAttribute(
          'aria-hidden',
          templateEditMode ? 'false' : 'true'
        );
      }
      if ($editTemplate) {
        $editTemplate.textContent = templateEditMode
          ? 'Cancel Update'
          : 'Edit Template';
      }
      if ($updateTemplate) {
        $updateTemplate.classList.toggle('hidden', !templateEditMode);
      }
      if ($templateMode) {
        $templateMode.classList.toggle('hidden', !templateEditMode);
      }
      if ($send) {
        $send.disabled = templateEditMode;
      }
      syncAiAssistControls();
      if (templateEditMode) {
        clearRewriteUndo();
        templateUpdateFlow.enterEditMode();
        const raw = templateUpdateFlow.getRawSource();
        rawSubject = raw.subject ?? rawSubject;
        rawHtml = raw.html ?? rawHtml;
        rawFontFamily = raw.fontFamily ?? rawFontFamily;
        host.querySelector('#ec-subject').value = rawSubject;
        if ($rawBody) {
          $rawBody.value = rawHtml;
          $rawBody.classList.toggle('hidden', !!quill);
        }
        if ($bodyContainer) {
          $bodyContainer.classList.toggle('hidden', !quill);
        }
        if (quill) {
          quill.root.innerHTML = rawHtml;
        }
        if (quill) {
          applyFontSizeToEditor(quill, DEFAULT_FONT_SIZE);
        }
        if (rawFontFamily && FONT_VALUE_SET.has(rawFontFamily)) {
          setFontSelection(rawFontFamily);
        }
        return;
      }
      rawSubject = host.querySelector('#ec-subject').value;
      rawHtml = quill?.root?.innerHTML ?? $rawBody?.value ?? rawHtml;
      templateUpdateFlow.exitEditMode({
        subject: rawSubject,
        html: rawHtml,
        fontFamily: selectedFont,
      });
      if ($rawBody) {
        if (quill) {
          $rawBody.classList.add('hidden');
        } else {
          $rawBody.value = viewHtml;
          $rawBody.classList.remove('hidden');
        }
      }
      if ($bodyContainer) {
        $bodyContainer.classList.toggle('hidden', !quill);
      }
      host.querySelector('#ec-subject').value = viewSubject;
      if (quill) {
        quill.root.innerHTML = viewHtml;
        applyFontSizeToEditor(quill, DEFAULT_FONT_SIZE);
      }
      if (viewFontFamily && FONT_VALUE_SET.has(viewFontFamily)) {
        setFontSelection(viewFontFamily);
      }
    };

    const onEditTemplate = () => {
      setTemplateEditMode(!templateEditMode);
    };

    /**
     * Copy selected merge variables as plain text so pasted tokens inherit the
     * destination editor's active formatting.
     *
     * @param {ClipboardEvent} event - Copy event raised inside the template guide.
     * @returns {void}
     */
    const onTemplateGuideCopy = (event) => {
      const selectedText = window.getSelection?.()?.toString() || '';
      if (!selectedText.trim() || !event.clipboardData) return;

      event.clipboardData.setData('text/plain', selectedText);
      event.preventDefault();
    };

    /**
     * Open Focus Mode step-template management without leaving the composer.
     *
     * @returns {Promise<void>}
     */
    const onManageStepTemplates = async () => {
      if (!canManageStepTemplates || templateEditMode) return;
      if ($manageStepTemplates) {
        $manageStepTemplates.disabled = true;
      }
      try {
        const { openFocusModeStepTemplatesModal } = await import(
          './focusModeStepTemplatesModal.js'
        );
        const result = await openFocusModeStepTemplatesModal({
          services: di,
          taskId: stepTemplateManagement.taskId,
          tenantId:
            stepTemplateManagement.tenantId ||
            di.tenant?.getTenantId?.() ||
            di.auth?.currentUser?.tenantId ||
            '',
          playbookId: stepTemplateManagement.playbookId || '',
          stepName: stepTemplateManagement.stepName || 'Current step',
          templateIds: allTemplates,
          isTargetBacked: stepTemplateManagement.isTargetBacked === true,
          enableTooltips,
        });
        const nextTemplates = normalizeEmailComposerTemplateIds(
          result?.templateIds
        );
        if (!nextTemplates.length) return;

        const previousTpl = currentTpl;
        allTemplates = nextTemplates;
        currentTpl = allTemplates.includes(previousTpl)
          ? previousTpl
          : result?.templateId || allTemplates[0] || '';
        syncTemplateSelector();
        stepTemplateManagement.onSaved?.({
          ...result,
          templateId: result?.templateId || allTemplates[0] || '',
          templateIds: allTemplates,
          templates: allTemplates,
          tpl: result?.templateId || allTemplates[0] || '',
        });

        if (currentTpl && currentTpl !== previousTpl) {
          await loadTemplate(currentTpl);
          renderAttachments();
          setTemplateControlsEnabled(updateEnabled);
          clearRewriteUndo();
          await setTemplateEditMode(false);
          host.querySelector('#ec-subject').value = viewSubject;
          if (quill) {
            quill.root.innerHTML = viewHtml;
            applyFontSizeToEditor(quill, DEFAULT_FONT_SIZE);
            setSelectedFont(viewFontFamily, {
              applyFormat: true,
              persist: false,
              force: true,
            });
          } else if ($rawBody && !$rawBody.classList.contains('hidden')) {
            $rawBody.value = viewHtml;
          }
        }

        di.errorUI?.global?.({
          text:
            result?.scope === 'task'
              ? 'Templates updated for this task.'
              : 'Step templates updated.',
          type: 'success',
          duration: 3000,
        });
      } catch (err) {
        console.error('[EmailComposer] step template management failed:', err);
        di.errorUI?.global?.({
          text: 'Unable to manage step templates right now.',
          type: 'error',
          duration: 4000,
        });
      } finally {
        if ($manageStepTemplates) {
          $manageStepTemplates.disabled = false;
        }
        if (host.classList.contains('hidden')) {
          modalService.open('emailComposerModal');
        }
      }
    };

    /**
     * Handle the Update Template button click.
     * @returns {Promise<void>}
     */
    const onUpdateTemplate = async () => {
      if (!currentTpl) return;
      if ($updateTemplate) {
        $updateTemplate.disabled = true;
        $updateTemplate.textContent = 'Updating…';
      }
      try {
        const updateResult = await templateUpdateFlow.confirmAndUpdate({
          templateId: currentTpl,
          subject: host.querySelector('#ec-subject').value,
          html: readCurrentHtml() || rawHtml,
          fontFamily: selectedFont,
        });
        if (!updateResult || updateResult.updated !== true) {
          if (updateResult?.reason === 'missing-tenant-id') {
            di.errorUI?.global?.({
              text: 'Cannot update template because tenant information is missing.',
              type: 'error',
              duration: 4000,
            });
          } else if (updateResult?.reason === 'update-disabled') {
            di.errorUI?.global?.({
              text: 'Template updates are unavailable for this step.',
              type: 'warning',
              duration: 3000,
            });
          }
          return;
        }
        updateEnabled = templateUpdateFlow.updateEnabled;
        const rendered = templateUpdateFlow.getRenderedView();
        const raw = templateUpdateFlow.getRawSource();
        rawSubject = raw.subject ?? rawSubject;
        rawHtml = raw.html ?? rawHtml;
        rawFontFamily = raw.fontFamily ?? rawFontFamily;
        viewSubject = rendered.subject ?? viewSubject;
        viewHtml = rendered.html ?? viewHtml;
        if (rendered.fontFamily && FONT_VALUE_SET.has(rendered.fontFamily)) {
          viewFontFamily = rendered.fontFamily;
          selectedFont = rendered.fontFamily;
        }
        renderedTemplateSnapshot = {
          subject: viewSubject,
          bodyText: htmlToPlainText(viewHtml),
          html: viewHtml,
        };
        if (updateResult.renderError) {
          console.warn(
            '[EmailComposer] template update preview failed:',
            updateResult.renderError
          );
          di.errorUI?.global?.({
            text: "Template saved, but preview rendering failed. Check the template's Mustache syntax.",
            type: 'warning',
            duration: 5000,
          });
        }
        clearRewriteUndo();
        await setTemplateEditMode(false);
        if (host.classList.contains('hidden')) {
          modalService.open('emailComposerModal');
        }
        const sendBtn = host.querySelector('#ec-send');
        sendBtn?.focus?.();
      } catch (err) {
        console.error('[EmailComposer] template update failed:', err);
        di.errorUI?.global?.({
          text: 'Failed to update template. Please try again or refresh the page.',
          type: 'error',
          duration: 4000,
        });
      } finally {
        if ($updateTemplate) {
          $updateTemplate.disabled = false;
          $updateTemplate.textContent = 'Update Template';
        }
      }
    };

    $send.addEventListener('click', onSend);
    $cancel.addEventListener('click', onCancel);
    $save?.addEventListener('click', onSave);
    $meeting?.addEventListener('change', updateSave);
    $event?.addEventListener('change', updateSave);
    $otherChk?.addEventListener('change', updateSave);
    $otherTxt?.addEventListener('input', updateSave);
    $updateTemplate?.addEventListener('click', onUpdateTemplate);
    $editTemplate?.addEventListener('click', onEditTemplate);
    $templateGuide?.addEventListener('copy', onTemplateGuideCopy);
    $manageStepTemplates?.addEventListener('click', onManageStepTemplates);
    const tplChange = async () => {
      currentTpl = $tplSel.value;
      await loadTemplate(currentTpl);
      renderAttachments();
      setTemplateControlsEnabled(updateEnabled);
      clearRewriteUndo();
      await setTemplateEditMode(false);
      host.querySelector('#ec-subject').value = viewSubject;
      if (quill) {
        quill.root.innerHTML = viewHtml;
        applyFontSizeToEditor(quill, DEFAULT_FONT_SIZE);
        setSelectedFont(viewFontFamily, {
          applyFormat: true,
          persist: false,
          force: true,
        });
      }
    };
    $tplSel?.addEventListener('change', tplChange);

    const onBrainstorm = async () => {
      if (!canBrainstorm || !brainstormCallable || templateEditMode) return;

      const requestSignature = currentDraftSignature();
      setAiAssistBusy('brainstorm');

      try {
        const response = await brainstormCallable(buildAiAssistRequest());
        const ideas = Array.isArray(response?.data?.ideas)
          ? response.data.ideas
          : [];

        if (currentDraftSignature() !== requestSignature) {
          clearBrainstormResults();
          showBrainstormStatus(
            'Draft changed while ideas were generating. Run it again for fresh coaching.',
            'warning'
          );
          return;
        }

        if (!ideas.length) {
          clearBrainstormResults();
          showBrainstormStatus(
            'No ideas were returned for this draft. Try again after adjusting the email.',
            'warning'
          );
          return;
        }

        renderBrainstormIdeas(ideas);
      } catch (err) {
        console.error('[EmailComposer] brainstorming failed:', err);
        clearBrainstormResults();
        showBrainstormStatus(
          'Unable to generate ideas right now. Please try again.',
          'error'
        );
      } finally {
        setAiAssistBusy('');
      }
    };
    $brainstormBtn?.addEventListener('click', onBrainstorm);
    $attachmentButton?.addEventListener('click', onAttachClick);
    $attachmentInput?.addEventListener('change', onAttachmentInputChange);
    renderAttachments();

    /**
     * Rewrite the current draft and apply it into the composer with undo support.
     *
     * @returns {Promise<void>}
     */
    const onRewrite = async () => {
      if (!canRewrite || !rewriteCallable || templateEditMode) return;

      const requestSignature = currentDraftSignature();
      const previousDraft = snapshotCurrentDraft();
      setAiAssistBusy('rewrite');

      try {
        const response = await rewriteCallable(buildAiAssistRequest());
        const rewritePayload = response?.data || {};
        const hasRewriteContent = Boolean(
          (typeof rewritePayload.subject === 'string' &&
            rewritePayload.subject.trim()) ||
            (typeof rewritePayload.bodyHtml === 'string' &&
              rewritePayload.bodyHtml.trim()) ||
            (typeof rewritePayload.bodyText === 'string' &&
              rewritePayload.bodyText.trim())
        );
        const rewrittenDraft = normalizeRewriteDraft(
          rewritePayload,
          previousDraft
        );

        if (currentDraftSignature() !== requestSignature) {
          showBrainstormStatus(
            'Draft changed while the rewrite was generating. Run it again for an updated rewrite.',
            'warning'
          );
          return;
        }

        if (!hasRewriteContent) {
          showBrainstormStatus(
            'No rewrite was returned for this draft. Try again after adjusting the email.',
            'warning'
          );
          return;
        }

        lastRewriteSnapshot = previousDraft;
        applyComposerDraft(rewrittenDraft);
        hideAiAssistStatus();
        syncAiAssistControls();
      } catch (err) {
        console.error('[EmailComposer] rewrite failed:', err);
        showBrainstormStatus(
          'Unable to rewrite this draft right now. Please try again.',
          'error'
        );
      } finally {
        setAiAssistBusy('');
      }
    };

    /**
     * Restore the pre-rewrite subject and body snapshot.
     *
     * @returns {void}
     */
    const onUndoRewrite = () => {
      if (!lastRewriteSnapshot || templateEditMode || aiBusyAction) return;
      applyComposerDraft(lastRewriteSnapshot);
      lastRewriteSnapshot = null;
      hideAiAssistStatus();
      syncAiAssistControls();
    };

    $rewriteBtn?.addEventListener('click', onRewrite);
    $undoRewriteBtn?.addEventListener('click', onUndoRewrite);

    updateSave();
    modalService.open('emailComposerModal');

    /* ── Quill lazy-load ───────────────────────────────────────────── */
    try {
      const quillReady = await ensureQuillLoaded();
      if (!quillReady) {
        throw new Error('Rich-text editor unavailable');
      }
    } catch (err) {
      console.warn(
        '[EmailComposer] rich editor unavailable, using fallback',
        err
      );
      showFallbackEditor(viewHtml);
      di.errorUI?.global?.({
        text: 'Rich-text editor unavailable. Using the basic editor instead.',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    const toolbarOptions = [
      [{ size: [] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ];

    const Font = window.Quill.import('formats/font');
    Font.whitelist = OUTLOOK_SAFE_FONTS.map((font) => font.value);
    window.Quill.register(Font, true);

    const SizeStyle = window.Quill.import('attributors/style/size');
    SizeStyle.whitelist = Array.from(FONT_SIZE_VALUE_SET);
    window.Quill.register(SizeStyle, true);

    quill = new Quill('#ec-body', {
      theme: 'snow',
      modules: { toolbar: toolbarOptions },
    });
    const toolbar = quill.getModule('toolbar');
    if ($editorShell && toolbar?.container instanceof HTMLElement) {
      $editorShell.insertBefore(toolbar.container, $bodyContainer);
      if ($fontControl) {
        $fontControl.classList.add(
          'ql-formats',
          'ec-font-family-control--toolbar'
        );
        $fontControl.classList.remove('flex', 'items-center', 'gap-2', 'mb-2');
        $fontControl
          .querySelector('label')
          ?.classList.remove('text-sm', 'font-medium', 'shrink-0');
        $fontSel?.classList.remove('input', 'flex-1', 'h-8', 'px-2', 'text-sm');
        toolbar.container.prepend($fontControl);
      }
    }
    quill.root.innerHTML = viewHtml;
    applyFontSizeToEditor(quill, DEFAULT_FONT_SIZE);
    setSelectedFont(viewFontFamily, {
      applyFormat: true,
      persist: false,
      force: true,
    });

    if (toolbar?.container) {
      const sizeSelect = toolbar.container.querySelector('select.ql-size');
      if (sizeSelect instanceof HTMLSelectElement) {
        sizeSelect.innerHTML = '';
        const sizePicker =
          sizeSelect.parentElement?.querySelector('.ql-picker.ql-size') ?? null;
        const pickerItems = Array.from(
          sizePicker?.querySelectorAll('.ql-picker-item') ?? []
        );

        FONT_SIZE_OPTIONS.forEach(({ label, value }) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          option.setAttribute('data-label', label);
          option.selected = value === '';
          sizeSelect.append(option);
        });

        pickerItems.forEach((item, index) => {
          const option = FONT_SIZE_OPTIONS[index];
          if (!(item instanceof HTMLElement) || !option) return;
          const pickerValue = option.value || '';
          item.setAttribute('data-value', pickerValue);
          item.setAttribute('data-label', option.label);
        });

        sizePickerLabel = sizePicker?.querySelector('.ql-picker-label') ?? null;
        const defaultSizeLabel = FONT_SIZE_LABEL_MAP.get('') ?? '';
        if (sizePickerLabel instanceof HTMLElement && defaultSizeLabel) {
          sizePickerLabel.setAttribute('data-value', '');
          sizePickerLabel.setAttribute('data-label', defaultSizeLabel);
        }
      }
    }

    toolbar?.addHandler('font', (value) => {
      setSelectedFont(value || DEFAULT_FONT, {
        applyFormat: true,
        persist: true,
        force: true,
      });
    });

    toolbar?.addHandler('size', (value) => {
      if (!quill) return;
      const normalized = typeof value === 'string' && value.length ? value : '';
      if (!normalized) {
        quill.format('size', false);
        if (sizePickerLabel instanceof HTMLElement) {
          const fallbackLabel =
            FONT_SIZE_LABEL_MAP.get('') ?? FONT_SIZE_OPTIONS[0].label;
          sizePickerLabel.setAttribute('data-value', '');
          sizePickerLabel.setAttribute('data-label', fallbackLabel);
        }
        return;
      }
      if (FONT_SIZE_VALUE_SET.has(normalized)) {
        quill.format('size', normalized);
        if (sizePickerLabel instanceof HTMLElement) {
          const friendlyLabel =
            FONT_SIZE_LABEL_MAP.get(normalized) ||
            normalized ||
            FONT_SIZE_OPTIONS[0].label;
          sizePickerLabel.setAttribute('data-value', normalized);
          sizePickerLabel.setAttribute('data-label', friendlyLabel);
        }
        return;
      }
      if (sizePickerLabel instanceof HTMLElement) {
        const fallbackLabel =
          FONT_SIZE_LABEL_MAP.get('') ?? FONT_SIZE_OPTIONS[0].label;
        sizePickerLabel.setAttribute('data-value', '');
        sizePickerLabel.setAttribute('data-label', fallbackLabel);
      }
    });

    /* ── dynamic grow-shrink setup ────────────────────────────── */
    const body = host.querySelector('#ec-body'); // .ql-container
    const editor =
      body?.querySelector('.ql-editor') ||
      (body?.classList?.contains('ql-editor') ? body : null);

    if (!(body instanceof HTMLElement) || !(editor instanceof HTMLElement)) {
      showFallbackEditor(viewHtml);
      return;
    }

    quill.on('text-change', () => {});
  });
}

/* ── fixUtf8 helper ─────────────────────────────────────────── */
function fixUtf8(str) {
  try {
    // escape() converts every byte to %XX; decodeURIComponent re-interprets as UTF-8
    return decodeURIComponent(escape(str));
  } catch {
    return str; // already OK → return unchanged
  }
}

/**
 * Convert HTML markup into plain text for draft-coaching requests.
 *
 * @param {string} [html=''] - HTML source from the editor or rendered template.
 * @returns {string} Plain-text representation of the HTML.
 */
function htmlToPlainText(html = '') {
  if (!html) return '';
  try {
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    return String(parsed.body?.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } catch {
    return String(html)
      .replace(/<[^>]+>/g, ' ')
      .trim();
  }
}

/**
 * Normalize editor content into a stable plain-text payload.
 *
 * @param {string} [value=''] - Editor text or HTML content.
 * @returns {string} Plain-text draft body.
 */
function normalizeEditorText(value = '') {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.includes('<')) {
    return htmlToPlainText(text);
  }
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Convert plain text into simple paragraph HTML for editor insertion.
 *
 * @param {string} [text=''] - Plain-text email body.
 * @returns {string} Lightweight HTML fragment.
 */
function plainTextToHtml(text = '') {
  const normalized = String(text || '').trim();
  if (!normalized) return '';

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map(
      (paragraph) =>
        `<p>${paragraph
          .split('\n')
          .map((line) => escapeAttr(line))
          .join('<br>')}</p>`
    )
    .join('');
}

/**
 * Normalize a candidate tag name into the email-safe subset used by rewrite sanitization.
 *
 * @param {string} [tagName=''] - Raw element tag name.
 * @returns {string} Normalized lower-case tag name.
 */
function normalizeSafeRewriteTagName(tagName = '') {
  const normalized = String(tagName || '')
    .trim()
    .toLowerCase();
  return REWRITE_TAG_ALIAS_MAP.get(normalized) || normalized;
}

/**
 * Normalize a user-visible text attribute for safe HTML insertion.
 *
 * @param {string | null | undefined} value - Raw attribute value.
 * @returns {string} Safe text attribute value.
 */
function sanitizeRewriteTextAttribute(value) {
  return String(value ?? '')
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Keep only absolute URLs that use the allowed protocols for AI rewrite content.
 *
 * @param {string | null | undefined} rawValue - Candidate attribute URL.
 * @param {Set<string>} allowedProtocols - Protocols allowed for this attribute.
 * @returns {string} Safe absolute URL or an empty string.
 */
function sanitizeRewriteUrl(rawValue, allowedProtocols) {
  const trimmed = String(rawValue || '').trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    if (allowedProtocols.has(parsed.protocol)) {
      return parsed.toString();
    }
  } catch {
    return '';
  }

  return '';
}

/**
 * Keep only numeric image dimensions so rewrite output cannot smuggle CSS through attributes.
 *
 * @param {string | null | undefined} rawValue - Candidate width/height attribute.
 * @returns {string} Safe numeric dimension or an empty string.
 */
function sanitizeRewriteDimension(rawValue) {
  const trimmed = String(rawValue || '').trim();
  return SAFE_REWRITE_DIMENSION_PATTERN.test(trimmed) ? trimmed : '';
}

/**
 * Recursively sanitize one DOM node from the AI rewrite payload.
 *
 * @param {Node} node - Parsed source DOM node.
 * @param {Document} documentRef - Safe document used to construct the sanitized tree.
 * @returns {Node} Sanitized node or fragment.
 */
function sanitizeAiRewriteNode(node, documentRef) {
  if (!node || !documentRef) {
    return document.implementation
      .createHTMLDocument('')
      .createDocumentFragment();
  }

  if (node.nodeType === Node.TEXT_NODE) {
    return documentRef.createTextNode(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return documentRef.createDocumentFragment();
  }

  const sourceElement = /** @type {HTMLElement} */ (node);
  const tagName = normalizeSafeRewriteTagName(sourceElement.tagName);

  if (STRIP_REWRITE_HTML_TAGS.has(tagName)) {
    return documentRef.createDocumentFragment();
  }

  if (!SAFE_REWRITE_HTML_TAGS.has(tagName)) {
    const fragment = documentRef.createDocumentFragment();
    Array.from(sourceElement.childNodes).forEach((childNode) => {
      fragment.appendChild(sanitizeAiRewriteNode(childNode, documentRef));
    });
    return fragment;
  }

  if (tagName === 'img') {
    const src = sanitizeRewriteUrl(
      sourceElement.getAttribute('src'),
      SAFE_REWRITE_IMAGE_PROTOCOLS
    );
    if (!src) {
      return documentRef.createDocumentFragment();
    }

    const image = documentRef.createElement('img');
    image.setAttribute('src', src);

    const alt = sanitizeRewriteTextAttribute(sourceElement.getAttribute('alt'));
    if (alt) {
      image.setAttribute('alt', alt);
    }

    const width = sanitizeRewriteDimension(sourceElement.getAttribute('width'));
    if (width) {
      image.setAttribute('width', width);
    }

    const height = sanitizeRewriteDimension(
      sourceElement.getAttribute('height')
    );
    if (height) {
      image.setAttribute('height', height);
    }

    return image;
  }

  if (tagName === 'br') {
    return documentRef.createElement('br');
  }

  if (tagName === 'a') {
    const href = sanitizeRewriteUrl(
      sourceElement.getAttribute('href'),
      SAFE_REWRITE_LINK_PROTOCOLS
    );
    if (!href) {
      const fragment = documentRef.createDocumentFragment();
      Array.from(sourceElement.childNodes).forEach((childNode) => {
        fragment.appendChild(sanitizeAiRewriteNode(childNode, documentRef));
      });
      return fragment;
    }

    const link = documentRef.createElement('a');
    link.setAttribute('href', href);
    Array.from(sourceElement.childNodes).forEach((childNode) => {
      link.appendChild(sanitizeAiRewriteNode(childNode, documentRef));
    });
    return link;
  }

  const sanitizedElement = documentRef.createElement(tagName);
  Array.from(sourceElement.childNodes).forEach((childNode) => {
    sanitizedElement.appendChild(sanitizeAiRewriteNode(childNode, documentRef));
  });
  return sanitizedElement;
}

/**
 * Sanitize AI rewrite HTML to the narrow email-safe subset supported by the composer.
 *
 * @param {string} [html=''] - Raw AI rewrite HTML.
 * @returns {string} Sanitized HTML fragment.
 */
function sanitizeAiRewriteHtml(html = '') {
  const raw = String(html || '').trim();
  if (!raw) return '';

  try {
    const parsed = new DOMParser().parseFromString(raw, 'text/html');
    const safeDocument = document.implementation.createHTMLDocument(
      'sanitized-ai-rewrite'
    );
    const container = safeDocument.createElement('div');

    Array.from(parsed.body?.childNodes || []).forEach((childNode) => {
      container.appendChild(sanitizeAiRewriteNode(childNode, safeDocument));
    });

    return container.innerHTML.trim();
  } catch {
    return '';
  }
}

/**
 * Decide when sanitized rewrite HTML lost enough content that plain-text fallback is safer.
 *
 * @param {string} [sanitizedHtml=''] - Sanitized rewrite HTML.
 * @param {string} [bodyText=''] - Plain-text rewrite body from the callable.
 * @returns {boolean} True when the composer should rebuild HTML from plain text.
 */
function shouldFallbackToPlainTextRewrite(sanitizedHtml = '', bodyText = '') {
  const normalizedBodyText = normalizeEditorText(bodyText);
  if (!normalizedBodyText) return false;

  const sanitizedText = normalizeEditorText(sanitizedHtml);
  if (!sanitizedText) return true;

  return (
    sanitizedText.length <
    Math.ceil(normalizedBodyText.length * SAFE_REWRITE_FALLBACK_TEXT_RATIO)
  );
}

/**
 * Normalize composer template identifiers into a stable ordered set.
 *
 * @param {Iterable<string>|string|null|undefined} templateIds - Candidate ids.
 * @returns {string[]} Ordered unique template ids.
 */
function normalizeEmailComposerTemplateIds(templateIds) {
  const values =
    typeof templateIds === 'string' || !templateIds?.[Symbol.iterator]
      ? [templateIds]
      : Array.from(templateIds);
  return values.reduce((acc, entry) => {
    const value = String(entry ?? '').trim();
    if (!value || acc.includes(value)) return acc;
    acc.push(value);
    return acc;
  }, []);
}

/**
 * Render the in-composer template selector options.
 *
 * @param {string[]} templateIds - Ordered template ids.
 * @param {string} currentTpl - Active template id.
 * @returns {string} HTML option markup.
 */
function renderTemplateOptions(templateIds = [], currentTpl = '') {
  return normalizeEmailComposerTemplateIds(templateIds)
    .map(
      (id) =>
        `<option value="${escapeAttr(id)}" ${
          id === currentTpl ? 'selected' : ''
        }>${escapeAttr(id)}</option>`
    )
    .join('');
}

function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
